// Lib
import { useCallback } from 'react';

// Services
import { ErrorCategory, handleError } from '@services/errorService';

// Graph
import {
  authGroupCreate,
  authGroupManageMultipleGroupsForAuth,
  authGroupUpdate,
  authGroupsUpdateBulk,
} from '@graphql/mutations/group';
import { authIndex } from '@graphql/mutations/campaign';

// Api
import client from '@api';

// Types
import type {
  Auth,
  AuthGroup,
  GqlAuthSource,
  GqlMutationRootAuthGroupCreateArgs,
  GqlMutationRootAuthGroupManageMultipleGroupsForAuthArgs,
  GqlMutationRootAuthGroupUpdateArgs,
  GqlMutationRootAuthGroupsUpdateBulkArgs,
  GqlMutationRootAuthIndexArgs,
} from '@app-types/index';
import type {
  AuthGroupCreateResponse,
  AuthGroupManageMultipleGroupsForAuthResponse,
  AuthGroupUpdateResponse,
  AuthGroupsUpdateBulkResponse,
  AuthIndexResponse,
  AuthInput,
} from './types';

interface UseGroupOperationsParams {
  groups: AuthGroup[];
  setGroups: React.Dispatch<React.SetStateAction<AuthGroup[]>>;
  setIsSaveToGroup: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useGroupOperations = ({
  groups,
  setGroups,
  setIsSaveToGroup,
}: UseGroupOperationsParams) => {
  const onAddToGroup = useCallback(
    async (
      groupId: string,
      { authId, innerId, source }: AuthInput,
      groups: AuthGroup[],
      callback: () => void = () => {},
      onSetGroups?: React.Dispatch<React.SetStateAction<AuthGroup[]>>,
      onSetSelectedAuth?: (auth: Auth) => void,
    ): Promise<void> => {
      let addedAuthId = authId;
      const group = groups.find(({ _id }) => _id === groupId);

      try {
        if (!group?.authIds?.includes(addedAuthId || '')) {
          if (!authId) {
            setIsSaveToGroup(true);
            const { data } = await client().mutate<
              AuthIndexResponse,
              GqlMutationRootAuthIndexArgs
            >({
              mutation: authIndex,
              variables: {
                input: {
                  innerId,
                  source: source as GqlAuthSource,
                },
              },
            });

            if (data && data?.authIndex) {
              addedAuthId = data?.authIndex?._id || '';
              onSetSelectedAuth?.(data?.authIndex);
            }
          }

          if (group) {
            const { data } = await client().mutate<
              AuthGroupUpdateResponse,
              GqlMutationRootAuthGroupUpdateArgs
            >({
              mutation: authGroupUpdate,
              fetchPolicy: 'no-cache',
              variables: {
                input: {
                  authGroupId: groupId,
                  addedAuthIds: addedAuthId ? [addedAuthId] : undefined,
                  name: group?.name,
                },
              },
            });

            if (data && data?.authGroupUpdate) {
              onSetGroups?.((prev) => [
                data?.authGroupUpdate,
                ...prev.filter(({ _id }) => _id !== data?.authGroupUpdate?._id),
              ]);

              setGroups((prev) =>
                prev.map((item) =>
                  item._id === data?.authGroupUpdate?._id
                    ? { ...item, authIds: data?.authGroupUpdate?.authIds }
                    : item,
                ),
              );
            }
          }
          callback();
          setIsSaveToGroup(false);
        }
      } catch (error) {
        handleError(error, { category: ErrorCategory.GRAPHQL, silent: true });
        setIsSaveToGroup(false);
      }
    },
    [setGroups, setIsSaveToGroup],
  );

  const onAddToGroups = useCallback(
    async (
      groupIds: string[],
      auths: Auth[],
      callback: () => void = () => {},
      isSaveToMaster: boolean = true,
    ): Promise<void> => {
      const addedAuthIds = auths.map(({ _id }) => _id);

      try {
        if (!groups.find(({ name }) => name === 'master')) {
          const { data } = await client().mutate<
            AuthGroupCreateResponse,
            GqlMutationRootAuthGroupCreateArgs
          >({
            mutation: authGroupCreate,
            variables: {
              input: {
                name: 'master',
              },
            },
          });
          if (data && data?.authGroupCreate) {
            setGroups((prev) => [data?.authGroupCreate, ...prev]);
          }
        } else {
          const { data } = await client().mutate<
            AuthGroupsUpdateBulkResponse,
            GqlMutationRootAuthGroupsUpdateBulkArgs
          >({
            mutation: authGroupsUpdateBulk,
            fetchPolicy: 'no-cache',
            variables: {
              input: {
                authGroupIds: [
                  ...groupIds,
                  ...(isSaveToMaster
                    ? [groups.find(({ name }) => name === 'master')?._id]
                    : []),
                ],
                addedAuthIds,
              },
            },
          });

          if (data && data?.authGroupsUpdateBulk) {
            setGroups((prev) =>
              prev.map((item) => {
                const update = data?.authGroupsUpdateBulk.find(
                  (group: AuthGroup) => group._id === item._id,
                );

                return update ? { ...item, authIds: update?.authIds } : item;
              }),
            );
          }
        }
        callback();
      } catch (error) {
        handleError(error, { category: ErrorCategory.GRAPHQL, silent: true });
        setIsSaveToGroup(false);
      }
    },
    [groups, setGroups, setIsSaveToGroup],
  );

  const onCreateGroup = useCallback(
    async (
      groupName: string,
      callback?: (group: AuthGroup) => void,
      authIds: string[] = [],
    ): Promise<void> => {
      const { data } = await client().mutate<
        AuthGroupCreateResponse,
        GqlMutationRootAuthGroupCreateArgs
      >({
        mutation: authGroupCreate,
        variables: {
          input: {
            name: groupName,
            authIds: [],
            ...(authIds ? { authIds } : {}),
          },
        },
      });
      if (data && data?.authGroupCreate) {
        setGroups((prev) => [data?.authGroupCreate, ...prev]);
        callback?.(data?.authGroupCreate);
      }
    },
    [setGroups],
  );

  const onUpdateAuthInGroups = useCallback(
    async (
      authId: string,
      addedGroupIds?: string[],
      removedGroupIds?: string[],
      callback?: () => void,
    ): Promise<void> => {
      try {
        const { data } = await client().mutate<
          AuthGroupManageMultipleGroupsForAuthResponse,
          GqlMutationRootAuthGroupManageMultipleGroupsForAuthArgs
        >({
          mutation: authGroupManageMultipleGroupsForAuth,
          fetchPolicy: 'no-cache',
          variables: {
            input: {
              authId,
              ...(addedGroupIds ? { addedGroupIds } : {}),
              ...(removedGroupIds ? { removedGroupIds } : {}),
            },
          },
        });

        if (data && data?.authGroupManageMultipleGroupsForAuth) {
          setGroups((prev) =>
            prev.map((group) => {
              if (
                removedGroupIds?.length &&
                removedGroupIds.includes(group?._id)
              ) {
                return {
                  ...group,
                  authIds: group?.authIds?.filter((id) => id !== authId),
                };
              }

              if (addedGroupIds?.length && addedGroupIds.includes(group?._id)) {
                return {
                  ...group,
                  authIds: [...(group?.authIds || []), authId],
                };
              }

              return group;
            }),
          );
        }

        callback?.();
      } catch (error) {
        handleError(error, { category: ErrorCategory.GRAPHQL, silent: true });
        setIsSaveToGroup(false);
      }
    },
    [setGroups, setIsSaveToGroup],
  );

  return {
    onAddToGroup,
    onAddToGroups,
    onCreateGroup,
    onUpdateAuthInGroups,
  };
};
